import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

const api = new API();

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};

const getResponseData = (
    response
) => {
    /*
    |--------------------------------------------------------------------------
    | Supports:
    |
    | Axios:
    | response.data
    |
    | Custom API wrapper:
    | response.data.data
    |
    | Direct object:
    | response
    |--------------------------------------------------------------------------
    */

    const body =
        response?.data ??
        response ??
        {};

    return body;
};

const getPayloadData = (
    response
) => {
    const body =
        getResponseData(
            response
        );

    return (
        body?.data ??
        body
    );
};

const getPagination = (
    response
) => {
    const body =
        getResponseData(
            response
        );

    return (
        body?.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
        }
    );
};

const buildQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value ===
                    undefined ||
                value ===
                    null ||
                value ===
                    ""
            ) {
                return;
            }

            query.append(
                key,
                value
            );
        }
    );

    const queryString =
        query.toString();

    return queryString
        ? `?${queryString}`
        : "";
};

/*
|--------------------------------------------------------------------------
| FETCH ALL LEDGER ENTRIES
|--------------------------------------------------------------------------
*/

export const fetchSupplierLedgerEntries =
    createAsyncThunk(
        "supplierLedger/fetchEntries",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        search:
                            params.search,

                        supplierId:
                            params.supplierId,

                        transactionType:
                            params.transactionType,

                        direction:
                            params.direction,

                        status:
                            params.status,

                        financialYear:
                            params.financialYear,

                        documentType:
                            params.documentType,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierLedgerEntries()}${queryString}`
                    );

                return {
                    data:
                        getPayloadData(
                            response
                        ) || [],

                    pagination:
                        getPagination(
                            response
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier ledger."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH GLOBAL SUMMARY
|--------------------------------------------------------------------------
*/

export const fetchSupplierLedgerSummary =
    createAsyncThunk(
        "supplierLedger/fetchSummary",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        search:
                            params.search,

                        supplierId:
                            params.supplierId,

                        transactionType:
                            params.transactionType,

                        direction:
                            params.direction,

                        status:
                            params.status,

                        financialYear:
                            params.financialYear,

                        documentType:
                            params.documentType,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierLedgerSummary()}${queryString}`
                    );

                return (
                    getPayloadData(
                        response
                    ) || {}
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier ledger summary."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH SUPPLIER BALANCE LIST
|--------------------------------------------------------------------------
|
| One row per supplier:
|
| Supplier
| Purchases
| Payments
| Debit Notes
| Outstanding
|--------------------------------------------------------------------------
*/

export const fetchSupplierBalanceList =
    createAsyncThunk(
        "supplierLedger/fetchSupplierBalances",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        search:
                            params.search,

                        financialYear:
                            params.financialYear,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierBalanceList()}${queryString}`
                    );

                return {
                    data:
                        getPayloadData(
                            response
                        ) || [],

                    pagination:
                        getPagination(
                            response
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier balances."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH ONE SUPPLIER LEDGER
|--------------------------------------------------------------------------
*/

export const fetchSupplierLedgerBySupplier =
    createAsyncThunk(
        "supplierLedger/fetchBySupplier",

        async (
            {
                supplierId,
                ...params
            },
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    throw new Error(
                        "Supplier ID is required."
                    );
                }

                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        search:
                            params.search,

                        transactionType:
                            params.transactionType,

                        direction:
                            params.direction,

                        status:
                            params.status,

                        financialYear:
                            params.financialYear,

                        documentType:
                            params.documentType,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierLedgerBySupplier(
                            supplierId
                        )}${queryString}`
                    );

                const data =
                    getPayloadData(
                        response
                    ) || {};

                return {
                    supplier:
                        data?.supplier ||
                        null,

                    balance:
                        data?.balance || {
                            totalDebit: 0,
                            totalCredit: 0,
                            balance: 0,
                        },

                    records:
                        Array.isArray(
                            data?.records
                        )
                            ? data.records
                            : [],

                    pagination:
                        getPagination(
                            response
                        ),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier ledger."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH SINGLE LEDGER ENTRY
|--------------------------------------------------------------------------
*/

export const fetchSupplierLedgerEntryById =
    createAsyncThunk(
        "supplierLedger/fetchEntryById",

        async (
            ledgerEntryId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !ledgerEntryId
                ) {
                    throw new Error(
                        "Ledger entry ID is required."
                    );
                }

                const response =
                    await api.get(
                        config.getSupplierLedgerEntryById(
                            ledgerEntryId
                        )
                    );

                return (
                    getPayloadData(
                        response
                    ) || null
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load ledger entry."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| RECALCULATE SUPPLIER LEDGER
|--------------------------------------------------------------------------
*/

export const recalculateSupplierLedger =
    createAsyncThunk(
        "supplierLedger/recalculate",

        async (
            supplierId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !supplierId
                ) {
                    throw new Error(
                        "Supplier ID is required."
                    );
                }

                const response =
                    await api.post(
                        config.recalculateSupplierLedger(
                            supplierId
                        ),
                        {}
                    );

                return (
                    getPayloadData(
                        response
                    ) || {}
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to recalculate supplier ledger."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    /*
    |--------------------------------------------------------------------------
    | ALL ENTRIES
    |--------------------------------------------------------------------------
    */

    entries: [],

    entriesPagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },

    /*
    |--------------------------------------------------------------------------
    | GLOBAL SUMMARY
    |--------------------------------------------------------------------------
    */

    summary: {
        totalEntries: 0,

        totalDebit: 0,

        totalCredit: 0,

        outstandingPayable: 0,

        purchaseBillAmount: 0,

        paymentOutAmount: 0,

        debitNoteAmount: 0,
    },

    /*
    |--------------------------------------------------------------------------
    | SUPPLIER BALANCE LIST
    |--------------------------------------------------------------------------
    */

    supplierBalances: [],

    supplierBalancesPagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
    },

    /*
    |--------------------------------------------------------------------------
    | SELECTED SUPPLIER
    |--------------------------------------------------------------------------
    */

    selectedSupplier: null,

    selectedSupplierBalance: {
        totalDebit: 0,
        totalCredit: 0,
        balance: 0,
    },

    selectedSupplierEntries: [],

    selectedSupplierPagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
    },

    /*
    |--------------------------------------------------------------------------
    | SINGLE ENTRY
    |--------------------------------------------------------------------------
    */

    selectedEntry: null,

    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    loading: {
        entries: false,

        summary: false,

        suppliers: false,

        supplierDetails: false,

        entryDetails: false,

        recalculate: false,
    },

    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    error: {
        entries: null,

        summary: null,

        suppliers: null,

        supplierDetails: null,

        entryDetails: null,

        recalculate: null,
    },

    recalculateResult: null,
};

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const supplierLedgerSlice =
    createSlice({
        name:
            "supplierLedger",

        initialState,

        reducers: {
            /*
            |--------------------------------------------------------------------------
            | CLEAR SELECTED SUPPLIER
            |--------------------------------------------------------------------------
            */

            clearSelectedSupplierLedger: (
                state
            ) => {
                state.selectedSupplier =
                    null;

                state.selectedSupplierBalance =
                    {
                        totalDebit: 0,

                        totalCredit: 0,

                        balance: 0,
                    };

                state.selectedSupplierEntries =
                    [];

                state.selectedSupplierPagination =
                    {
                        page: 1,

                        limit: 20,

                        total: 0,

                        totalPages: 1,
                    };

                state.error.supplierDetails =
                    null;
            },

            /*
            |--------------------------------------------------------------------------
            | CLEAR SELECTED ENTRY
            |--------------------------------------------------------------------------
            */

            clearSelectedSupplierLedgerEntry: (
                state
            ) => {
                state.selectedEntry =
                    null;

                state.error.entryDetails =
                    null;
            },

            /*
            |--------------------------------------------------------------------------
            | CLEAR ERRORS
            |--------------------------------------------------------------------------
            */

            clearSupplierLedgerErrors: (
                state
            ) => {
                state.error = {
                    entries: null,

                    summary: null,

                    suppliers: null,

                    supplierDetails: null,

                    entryDetails: null,

                    recalculate: null,
                };
            },

            /*
            |--------------------------------------------------------------------------
            | CLEAR RECALCULATE RESULT
            |--------------------------------------------------------------------------
            */

            clearSupplierLedgerRecalculateResult: (
                state
            ) => {
                state.recalculateResult =
                    null;

                state.error.recalculate =
                    null;
            },
        },

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | ALL ENTRIES
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierLedgerEntries.pending,
                    (
                        state
                    ) => {
                        state.loading.entries =
                            true;

                        state.error.entries =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierLedgerEntries.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.entries =
                            false;

                        state.entries =
                            Array.isArray(
                                action
                                    .payload
                                    ?.data
                            )
                                ? action
                                      .payload
                                      .data
                                : [];

                        state.entriesPagination =
                            action
                                .payload
                                ?.pagination ||
                            state.entriesPagination;
                    }
                )

                .addCase(
                    fetchSupplierLedgerEntries.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.entries =
                            false;

                        state.error.entries =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier ledger.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | SUMMARY
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierLedgerSummary.pending,
                    (
                        state
                    ) => {
                        state.loading.summary =
                            true;

                        state.error.summary =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierLedgerSummary.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.summary =
                            false;

                        state.summary = {
                            ...state.summary,

                            ...(action.payload ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchSupplierLedgerSummary.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.summary =
                            false;

                        state.error.summary =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier ledger summary.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | SUPPLIER BALANCES
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierBalanceList.pending,
                    (
                        state
                    ) => {
                        state.loading.suppliers =
                            true;

                        state.error.suppliers =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierBalanceList.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.suppliers =
                            false;

                        state.supplierBalances =
                            Array.isArray(
                                action
                                    .payload
                                    ?.data
                            )
                                ? action
                                      .payload
                                      .data
                                : [];

                        state.supplierBalancesPagination =
                            action
                                .payload
                                ?.pagination ||
                            state.supplierBalancesPagination;
                    }
                )

                .addCase(
                    fetchSupplierBalanceList.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.suppliers =
                            false;

                        state.error.suppliers =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier balances.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | ONE SUPPLIER LEDGER
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierLedgerBySupplier.pending,
                    (
                        state
                    ) => {
                        state.loading.supplierDetails =
                            true;

                        state.error.supplierDetails =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierLedgerBySupplier.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.supplierDetails =
                            false;

                        state.selectedSupplier =
                            action
                                .payload
                                ?.supplier ||
                            null;

                        state.selectedSupplierBalance =
                            action
                                .payload
                                ?.balance || {
                                totalDebit: 0,

                                totalCredit: 0,

                                balance: 0,
                            };

                        state.selectedSupplierEntries =
                            Array.isArray(
                                action
                                    .payload
                                    ?.records
                            )
                                ? action
                                      .payload
                                      .records
                                : [];

                        state.selectedSupplierPagination =
                            action
                                .payload
                                ?.pagination ||
                            state.selectedSupplierPagination;
                    }
                )

                .addCase(
                    fetchSupplierLedgerBySupplier.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.supplierDetails =
                            false;

                        state.error.supplierDetails =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier ledger.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | SINGLE ENTRY
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierLedgerEntryById.pending,
                    (
                        state
                    ) => {
                        state.loading.entryDetails =
                            true;

                        state.error.entryDetails =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierLedgerEntryById.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.entryDetails =
                            false;

                        state.selectedEntry =
                            action.payload ||
                            null;
                    }
                )

                .addCase(
                    fetchSupplierLedgerEntryById.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.entryDetails =
                            false;

                        state.error.entryDetails =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load ledger entry.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | RECALCULATE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    recalculateSupplierLedger.pending,
                    (
                        state
                    ) => {
                        state.loading.recalculate =
                            true;

                        state.error.recalculate =
                            null;

                        state.recalculateResult =
                            null;
                    }
                )

                .addCase(
                    recalculateSupplierLedger.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.recalculate =
                            false;

                        state.recalculateResult =
                            action.payload ||
                            null;
                    }
                )

                .addCase(
                    recalculateSupplierLedger.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.recalculate =
                            false;

                        state.error.recalculate =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to recalculate supplier ledger.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearSelectedSupplierLedger,

    clearSelectedSupplierLedgerEntry,

    clearSupplierLedgerErrors,

    clearSupplierLedgerRecalculateResult,
} =
    supplierLedgerSlice.actions;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
|
| IMPORTANT:
|
| These assume your root reducer uses:
|
| supplierLedger: supplierLedgerReducer
|--------------------------------------------------------------------------
*/

export const supplierLedgerStateSelector = (
    state
) =>
    state?.supplierLedger ||
    initialState;

export const supplierLedgerEntriesSelector = (
    state
) =>
    state?.supplierLedger
        ?.entries || [];

export const supplierLedgerEntriesPaginationSelector = (
    state
) =>
    state?.supplierLedger
        ?.entriesPagination ||
    initialState.entriesPagination;

export const supplierLedgerSummarySelector = (
    state
) =>
    state?.supplierLedger
        ?.summary ||
    initialState.summary;

export const supplierBalanceListSelector = (
    state
) =>
    state?.supplierLedger
        ?.supplierBalances ||
    [];

export const supplierBalancePaginationSelector = (
    state
) =>
    state?.supplierLedger
        ?.supplierBalancesPagination ||
    initialState
        .supplierBalancesPagination;

export const selectedSupplierLedgerSelector = (
    state
) => ({
    supplier:
        state?.supplierLedger
            ?.selectedSupplier ||
        null,

    balance:
        state?.supplierLedger
            ?.selectedSupplierBalance ||
        initialState
            .selectedSupplierBalance,

    records:
        state?.supplierLedger
            ?.selectedSupplierEntries ||
        [],

    pagination:
        state?.supplierLedger
            ?.selectedSupplierPagination ||
        initialState
            .selectedSupplierPagination,
});

export const selectedSupplierLedgerEntrySelector = (
    state
) =>
    state?.supplierLedger
        ?.selectedEntry ||
    null;

export const supplierLedgerLoadingSelector = (
    state
) =>
    state?.supplierLedger
        ?.loading ||
    initialState.loading;

export const supplierLedgerErrorSelector = (
    state
) =>
    state?.supplierLedger
        ?.error ||
    initialState.error;

export const supplierLedgerRecalculateResultSelector = (
    state
) =>
    state?.supplierLedger
        ?.recalculateResult ||
    null;

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default supplierLedgerSlice.reducer;